/**
 * Sudo Session Hook
 * Manages admin sudo/impersonation sessions for support debugging
 * 
 * IMPORTANT: This does NOT modify Firebase Auth tokens or authentication state.
 * It only stores client-side session data for admin tools to use when viewing
 * data "as" another user. The actual auth user remains the admin.
 */

import { useState, useEffect, useCallback } from 'react';
import { Timestamp } from 'firebase/firestore';
import type { SudoSessionState } from '@/types/admin';
import { logSudoStart, logSudoEnd } from '@/services/adminLogService';

const SUDO_SESSION_KEY = 'threadlock_admin_sudo_session';

/**
 * Hook to manage sudo session state
 * 
 * @param adminUserId - Current admin user ID
 * @returns Sudo session state and control functions
 * 
 * @example
 * ```tsx
 * const { sudoState, startSudo, endSudo, getEffectiveUserId } = useSudoSession(adminUserId);
 * 
 * // Start sudo for user
 * await startSudo('user123');
 * 
 * // Get user ID to use for queries (returns targetUserId if sudo active, else adminUserId)
 * const userId = getEffectiveUserId();
 * 
 * // End sudo session
 * await endSudo();
 * ```
 */
export function useSudoSession(adminUserId: string | null) {
  const [sudoState, setSudoState] = useState<SudoSessionState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Load sudo state from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(SUDO_SESSION_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as SudoSessionState;
        // Validate that the stored admin matches current admin
        if (parsed.adminUserId === adminUserId) {
          setSudoState(parsed);
        } else {
          // Different admin - clear stale sudo session
          localStorage.removeItem(SUDO_SESSION_KEY);
        }
      }
    } catch (err) {
      console.error('[useSudoSession] Failed to load sudo state from localStorage:', err);
      localStorage.removeItem(SUDO_SESSION_KEY);
    }
  }, [adminUserId]);

  /**
   * Start a sudo session for a target user
   */
  const startSudo = useCallback(
    async (targetUserId: string, targetEmail?: string): Promise<void> => {
      if (!adminUserId) {
        throw new Error('Admin user ID is required to start sudo session');
      }

      setLoading(true);
      setError(null);

      try {
        const newSudoState: SudoSessionState = {
          active: true,
          adminUserId,
          targetUserId,
          startedAt: Timestamp.now(),
        };

        // Save to localStorage
        localStorage.setItem(SUDO_SESSION_KEY, JSON.stringify(newSudoState));
        setSudoState(newSudoState);

        // Log the sudo start action
        await logSudoStart(adminUserId, targetUserId, targetEmail);
      } catch (err) {
        console.error('[useSudoSession] Failed to start sudo session:', err);
        setError(err instanceof Error ? err : new Error('Failed to start sudo session'));
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [adminUserId]
  );

  /**
   * End the current sudo session
   */
  const endSudo = useCallback(async (): Promise<void> => {
    if (!sudoState || !adminUserId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Log the sudo end action
      await logSudoEnd(adminUserId, sudoState.targetUserId);

      // Clear from localStorage and state
      localStorage.removeItem(SUDO_SESSION_KEY);
      setSudoState(null);
    } catch (err) {
      console.error('[useSudoSession] Failed to end sudo session:', err);
      setError(err instanceof Error ? err : new Error('Failed to end sudo session'));
      // Still clear the session even if logging fails
      localStorage.removeItem(SUDO_SESSION_KEY);
      setSudoState(null);
    } finally {
      setLoading(false);
    }
  }, [sudoState, adminUserId]);

  /**
   * Get the effective user ID to use for queries
   * Returns targetUserId if sudo is active, otherwise returns the admin's real user ID
   */
  const getEffectiveUserId = useCallback((): string | null => {
    if (sudoState?.active) {
      return sudoState.targetUserId;
    }
    return adminUserId;
  }, [sudoState, adminUserId]);

  /**
   * Check if sudo session is currently active
   */
  const isSudoActive = useCallback((): boolean => {
    return sudoState?.active === true;
  }, [sudoState]);

  return {
    sudoState,
    startSudo,
    endSudo,
    getEffectiveUserId,
    isSudoActive,
    loading,
    error,
  };
}
